import React, { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Palette, Upload, X, Image } from 'lucide-react';
import { applyBrandingTheme, useBranding } from '@/lib/branding';
import { getBrandName } from '@/lib/brandName';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function ColorInput({ label, value, onChange, testId }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="h-10 w-14 rounded border cursor-pointer"
          data-testid={`${testId}-picker`}
        />
        <Input
          value={value}
          onChange={e => {
            const v = e.target.value;
            if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(v);
          }}
          placeholder="#1A73E8"
          className="w-32 font-mono"
          data-testid={`${testId}-hex`}
        />
        <div className="h-10 w-10 rounded-lg border" style={{ backgroundColor: value }} />
      </div>
    </div>
  );
}

function ImageUpload({ label, currentUrl, onFileSelect, accept, maxSize, testId }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [imgError, setImgError] = useState(false);

  const handleSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > maxSize) { toast.error(`File exceeds ${maxSize / (1024 * 1024)}MB limit`); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setImgError(false);
    onFileSelect(f);
  };

  const displayUrl = preview || (currentUrl ? `${API_BASE}${currentUrl}` : null);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0">
          {displayUrl && !imgError ? (
            <img src={displayUrl} alt={label} className="h-full w-full object-contain" onError={() => setImgError(true)} />
          ) : (
            <Image className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
        <div>
          <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} data-testid={`${testId}-btn`}>
            <Upload className="h-3 w-3 mr-2" />{currentUrl && !imgError ? 'Change' : 'Upload'}
          </Button>
          {file && <p className="text-xs text-muted-foreground mt-1">{file.name} ({(file.size / 1024).toFixed(0)}KB)</p>}
          {currentUrl && !file && <p className="text-xs text-muted-foreground mt-1">Current: {currentUrl.split('/').pop()}</p>}
          <input ref={inputRef} type="file" accept={accept} onChange={handleSelect} className="hidden" />
        </div>
      </div>
    </div>
  );
}

export default function BrandingSettingsPage() {
  const { refresh: refreshBranding } = useBranding();
  const [form, setForm] = useState({
    organisation_name: getBrandName(),
    primary_colour: '#16A34A',
    secondary_colour: '#34A853',
    android_version: '1.0.0',
    ios_version: '1.0.0',
    logo_url: '',
    favicon_url: '',
    login_logo_url: '',
    role_labels: {},
  });
  const [roles, setRoles] = useState([]);
  const [original, setOriginal] = useState(null);
  const [files, setFiles] = useState({ logo: null, favicon: null, login_logo: null });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/settings/branding')
      .then(r => {
        const d = r.data.data;
        setForm(d);
        setOriginal(d);
      })
      .catch(e => {
        if (e.response?.status === 403) toast.error('Access denied — Super Admin only');
        else toast.error('Failed to load branding settings');
      })
      .finally(() => setLoading(false));
    api.get('/admin/roles', { params: { pageSize: 100, sortBy: 'name', sortDir: 'asc' } })
      .then(r => setRoles((r.data.items || r.data.data || []).map(x => x.name).filter(Boolean)))
      .catch(() => setRoles([]));
  }, []);

  const handleSave = async () => {
    if (form.organisation_name.trim().length < 2) { toast.error('Organisation name must be at least 2 characters'); return; }
    if (!/^#[0-9A-Fa-f]{6}$/.test(form.primary_colour)) { toast.error('Invalid primary colour hex'); return; }
    if (!/^#[0-9A-Fa-f]{6}$/.test(form.secondary_colour)) { toast.error('Invalid secondary colour hex'); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('organisation_name', form.organisation_name.trim());
      fd.append('primary_colour', form.primary_colour);
      fd.append('secondary_colour', form.secondary_colour);
      fd.append('android_version', (form.android_version || '').trim());
      fd.append('ios_version', (form.ios_version || '').trim());
      fd.append('role_labels', JSON.stringify(form.role_labels || {}));
      if (files.logo) fd.append('logo', files.logo);
      if (files.favicon) fd.append('favicon', files.favicon);
      if (files.login_logo) fd.append('login_logo', files.login_logo);

      const res = await api.put('/admin/settings/branding', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.data;
      setForm(d);
      setOriginal(d);
      setFiles({ logo: null, favicon: null, login_logo: null });

      // Apply theme colours globally via centralized tokens
      applyBrandingTheme(d.primary_colour, d.secondary_colour);

      // Refresh branding context so sidebar/header/name update immediately
      await refreshBranding();

      toast.success('Branding settings saved');
    } catch (e) {
      const err = e.response?.data?.error || e.response?.data?.detail?.error || {};
      toast.error(err.message || 'Failed to save branding settings');
    }
    setSaving(false);
  };

  const handleCancel = () => {
    if (original) setForm(original);
    setFiles({ logo: null, favicon: null, login_logo: null });
  };

  if (loading) return <div className="flex items-center justify-center py-16"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="branding-settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Palette className="w-6 h-6" />Platform Branding</h1>
        <p className="text-muted-foreground mt-1">Manage the organisation identity and platform appearance</p>
      </div>

      {/* Section 1: Organisation Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Organisation Information</CardTitle>
          <CardDescription>Your organisation name and logo assets</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label>Organisation Name *</Label>
            <Input
              value={form.organisation_name}
              onChange={e => setForm(p => ({ ...p, organisation_name: e.target.value }))}
              placeholder="Enter organisation name"
              maxLength={120}
              data-testid="org-name-input"
            />
            <p className="text-xs text-muted-foreground mt-1">{form.organisation_name.length}/120 characters</p>
          </div>

          <ImageUpload
            label="Platform Logo"
            currentUrl={form.logo_url}
            onFileSelect={f => setFiles(p => ({ ...p, logo: f }))}
            accept=".png,.jpg,.jpeg,.svg"
            maxSize={2 * 1024 * 1024}
            testId="logo-upload"
          />

          <ImageUpload
            label="Favicon"
            currentUrl={form.favicon_url}
            onFileSelect={f => setFiles(p => ({ ...p, favicon: f }))}
            accept=".png,.ico"
            maxSize={1 * 1024 * 1024}
            testId="favicon-upload"
          />

          <ImageUpload
            label="Login Logo"
            currentUrl={form.login_logo_url}
            onFileSelect={f => setFiles(p => ({ ...p, login_logo: f }))}
            accept=".png,.jpg,.jpeg,.svg"
            maxSize={2 * 1024 * 1024}
            testId="login-logo-upload"
          />
        </CardContent>
      </Card>

      {/* Section 2: Theme Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Theme Settings</CardTitle>
          <CardDescription>Customize the platform colour scheme</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <ColorInput
            label="Primary Theme Colour *"
            value={form.primary_colour}
            onChange={v => setForm(p => ({ ...p, primary_colour: v }))}
            testId="primary-colour"
          />
          <ColorInput
            label="Secondary Theme Colour *"
            value={form.secondary_colour}
            onChange={v => setForm(p => ({ ...p, secondary_colour: v }))}
            testId="secondary-colour"
          />

          {/* Live preview */}
          <div className="pt-3 border-t">
            <p className="text-xs font-medium text-muted-foreground mb-2">Preview</p>
            <div className="flex gap-3">
              <div className="rounded-lg px-4 py-2 text-white text-sm font-medium" style={{ backgroundColor: form.primary_colour }}>Primary Button</div>
              <div className="rounded-lg px-4 py-2 text-white text-sm font-medium" style={{ backgroundColor: form.secondary_colour }}>Secondary Button</div>
              <div className="rounded-lg px-4 py-2 text-sm font-medium border" style={{ color: form.primary_colour, borderColor: form.primary_colour }}>Outline</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Mobile App Versions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mobile App Versions</CardTitle>
          <CardDescription>Version numbers shown inside the {getBrandName()} mobile apps</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5 sm:grid-cols-2">
          <div>
            <Label>Android Version</Label>
            <Input
              value={form.android_version || ''}
              onChange={e => setForm(p => ({ ...p, android_version: e.target.value }))}
              placeholder="e.g. 1.0.0"
              maxLength={20}
              data-testid="android-version-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Displayed on Android devices</p>
          </div>
          <div>
            <Label>iOS Version</Label>
            <Input
              value={form.ios_version || ''}
              onChange={e => setForm(p => ({ ...p, ios_version: e.target.value }))}
              placeholder="e.g. 1.0.0"
              maxLength={20}
              data-testid="ios-version-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Displayed on iOS devices</p>
          </div>
        </CardContent>
      </Card>

      {/* Section 4: Role Labels */}
      {roles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Role Labels</CardTitle>
            <CardDescription>Change how role names are shown. Display only — permissions stay with the role. Leave blank to show the role name.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            {roles.map(name => (
              <div key={name}>
                <Label>{name}</Label>
                <Input
                  value={(form.role_labels || {})[name] || ''}
                  onChange={e => setForm(p => ({ ...p, role_labels: { ...(p.role_labels || {}), [name]: e.target.value } }))}
                  placeholder={name}
                  maxLength={60}
                  data-testid={`role-label-input-${name.replace(/\s+/g, '-').toLowerCase()}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pb-8">
        <Button variant="outline" onClick={handleCancel} data-testid="branding-cancel-btn">Cancel</Button>
        <Button onClick={handleSave} disabled={saving} data-testid="branding-save-btn">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
